import {
  Button,
  List,
  ListItem,
  ListItemButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React from 'react';
import { P, match } from 'ts-pattern';
import TablerIconChevronRight from '~icons/tabler/chevron-right';
import { type APIResponse, aiChatsQueryOptions } from '#/api';
import { cn } from '#/utils/styles';

export interface ChatHistoryPanelProps {
  /**
   * Callback function to handle the selection of a chat.
   * @param chat - The chat object to be selected.
   */
  onSelect: (chat: APIResponse<'GET /_prv/ai/chats'>[number]) => void;
}

function ChatHistoryPanel({ onSelect }: ChatHistoryPanelProps) {
  const [open, setOpen] = React.useState(false);
  const chatsQuery = useQuery({
    ...aiChatsQueryOptions(),
    enabled: open,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchInterval: 1000 * 60 * 5,
  });

  const toggleOpen = React.useCallback(() => setOpen((prev) => !prev), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to refetch when the panel is opened
  React.useEffect(() => {
    if (open && chatsQuery.status === 'success') {
      chatsQuery.refetch();
    }
  }, [open, chatsQuery.status]);

  return (
    <div className='absolute bottom-0 left-0 right-0 p-4 px-4'>
      <Tooltip
        placement='top'
        title={match(chatsQuery)
          .with(
            { status: 'success', data: P.when((data) => _.isEmpty(data)) },
            () => 'No past chats'
          )
          .with(
            { status: 'success', data: P.when((data) => !_.isEmpty(data)) },
            (d) => `View ${d.data.length} past chats`
          )
          .with({ status: 'error' }, () => 'Unable to load past chats')
          .with({ status: 'pending' }, () => 'Loading past chats')
          .otherwise(() => undefined)}
        enterDelay={700}
        arrow
      >
        <span>
          <Button
            variant='text'
            color='inherit'
            size='small'
            disabled={match(chatsQuery)
              .with(
                {
                  status: 'success',
                  data: P.when(_.isEmpty),
                },
                () => true
              )
              .with({ status: 'pending' }, () => true)
              .with({ status: 'error' }, () => true)
              .otherwise(() => false)}
            className='px-2'
            endIcon={
              <TablerIconChevronRight
                className={cn({
                  'rotate-90': open,
                })}
              />
            }
            onClick={toggleOpen}
          >
            Past Chats
          </Button>
        </span>
      </Tooltip>
      {chatsQuery.status === 'success' && open && (
        <List dense disablePadding className='max-h-[400px] overflow-y-auto'>
          {chatsQuery.data.map((chat) => (
            <ListItem disablePadding key={chat.id}>
              <ListItemButton
                disableGutters
                className='px-2'
                onClick={() => onSelect(chat)}
              >
                <Typography variant='body1' noWrap>
                  {chat.title}
                </Typography>
                <div className='flex-1' />
                <Typography
                  variant='body2'
                  color='text.secondary'
                  textAlign='right'
                >
                  {DateTime.fromISO(chat.createdAt).toRelative({
                    style: 'long',
                  })}
                </Typography>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </div>
  );
}

export default ChatHistoryPanel;
